import { useEffect, useRef, useState } from 'react';
import { dayTickLabel } from '../../utils/periods';

// A single compact control that picks a { from, to } range in one popover calendar:
// click a start day, then an end day (order-independent; hover previews the span). Days
// outside [min, max] (the available data range) are disabled. Values are 'YYYY-MM-DD'.
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`; // m is 0-based

function parse(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return { y, m: m - 1, d };
}
const stamp = (o) => new Date(o.y, o.m, o.d).getTime();
const stampS = (s) => { const o = parse(s); return o ? stamp(o) : null; };

export function DateRangePicker({ from, to, onChange, min, max, compact = true }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null); // first clicked day (ymd) while selecting
  const [hover, setHover] = useState(null);

  const minD = parse(min);
  const maxD = parse(max);
  const today = new Date();
  const todayO = { y: today.getFullYear(), m: today.getMonth(), d: today.getDate() };
  const base = parse(to) || parse(from) || maxD || todayO;
  const [view, setView] = useState({ y: base.y, m: base.m });

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openCal = () => {
    const b = parse(to) || parse(from) || maxD || todayO;
    setView({ y: b.y, m: b.m });
    setAnchor(null); setHover(null);
    setOpen(true);
  };

  const label = (from && to) ? `${dayTickLabel(from)} – ${dayTickLabel(to)}` : 'Select range';

  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const inRange = (o) => {
    const t = stamp(o);
    if (minD && t < stamp(minD)) return false;
    if (maxD && t > stamp(maxD)) return false;
    return true;
  };

  const canPrev = !minD || new Date(view.y, view.m, 1) > new Date(minD.y, minD.m, 1);
  const canNext = !maxD || new Date(view.y, view.m, 1) < new Date(maxD.y, maxD.m, 1);
  const shift = (delta) => setView((v) => {
    const dt = new Date(v.y, v.m + delta, 1);
    return { y: dt.getFullYear(), m: dt.getMonth() };
  });

  const pick = (d) => {
    const clicked = ymd(view.y, view.m, d);
    if (!anchor) { setAnchor(clicked); setHover(clicked); return; }
    const a = stampS(anchor);
    const b = stampS(clicked);
    onChange({ from: a <= b ? anchor : clicked, to: a <= b ? clicked : anchor });
    setAnchor(null); setHover(null); setOpen(false);
  };

  // The span to highlight: while selecting it's anchor…hover, otherwise the committed from…to.
  const [spanLo, spanHi] = anchor
    ? (stampS(anchor) <= stampS(hover || anchor) ? [anchor, hover || anchor] : [hover || anchor, anchor])
    : [from, to];
  const loT = stampS(spanLo);
  const hiT = stampS(spanHi);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openCal())}
        className={`flex items-center justify-between gap-1.5 whitespace-nowrap rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40 ${compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
      >
        <span className="flex items-center gap-1.5">
          <svg className={`text-slate-400 dark:text-slate-500 ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="4" width="14" height="13" rx="2" /><path d="M3 8h14M7 3v3M13 3v3" strokeLinecap="round" />
          </svg>
          {label}
        </span>
        <svg className={`text-slate-400 dark:text-slate-500 ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.2 7.2a.75.75 0 011.06.02L10 11.06l3.74-3.84a.75.75 0 111.08 1.04l-4.28 4.4a.75.75 0 01-1.08 0l-4.28-4.4a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl bg-white dark:bg-slate-800 p-3 shadow-xl ring-1 ring-slate-200 dark:ring-white/10">
          <div className="flex items-center justify-between px-1">
            <button type="button" disabled={!canPrev} onClick={() => shift(-1)} className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30" aria-label="Previous month">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 5l-5 5 5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <div className="text-sm font-semibold text-navy-800 dark:text-slate-100">
              {new Date(view.y, view.m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" disabled={!canNext} onClick={() => shift(1)} className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30" aria-label="Next month">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>

          <div className="mt-2 grid grid-cols-7 gap-1 text-center text-xs text-slate-400 dark:text-slate-500">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-y-1 text-center text-sm">
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const o = { y: view.y, m: view.m, d };
              const t = stamp(o);
              const enabled = inRange(o);
              const isStart = loT != null && t === loT;
              const isEnd = hiT != null && t === hiT;
              const isEnd0 = isStart || isEnd;
              const inSel = loT != null && hiT != null && t >= loT && t <= hiT;
              const midSel = inSel && !isEnd0;
              return (
                <div key={`d${d}`} className={`${inSel ? 'bg-brand/10 dark:bg-brand/20' : ''} ${isStart && hiT !== loT ? 'rounded-l-lg' : ''} ${isEnd && hiT !== loT ? 'rounded-r-lg' : ''} ${loT === hiT && isEnd0 ? 'rounded-lg' : ''}`}>
                  <button
                    type="button"
                    disabled={!enabled}
                    onMouseEnter={() => { if (anchor) setHover(ymd(view.y, view.m, d)); }}
                    onClick={() => pick(d)}
                    className={`h-8 w-8 rounded-lg leading-8 transition ${
                      isEnd0 ? 'bg-brand text-white font-semibold'
                        : midSel ? 'text-brand-dark dark:text-brand-light'
                          : enabled ? 'text-slate-700 dark:text-slate-300 hover:bg-brand/10 dark:hover:bg-brand/20' : 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                    }`}
                  >
                    {d}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-3 px-1 text-[11px] text-slate-400 dark:text-slate-500">
            {anchor ? 'Now pick the end date' : 'Click a start date, then an end date'}
          </div>
        </div>
      )}
    </div>
  );
}
